import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import classNames from 'classnames';
import React, { FC, useRef, useState } from 'react';
import { usersApi } from 'src/api/user';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import styles from './UploadImage.module.scss';

interface UploadImageProps {
  defaultImage: string;
  onUpdate: (fileId: string) => void;
  classNameImageContainer?: string;
  children?: React.ReactNode;
  helperText?: string;
  hideDesc?: boolean;
  commentType?: string;
  error?: string;
  /** Section heading; pass an empty string to render none. */
  title?: string;
  /** Color behind the image, e.g. to preview a light logo on a dark bar. */
  backgroundColor?: string;
}

export const UploadImage: FC<UploadImageProps> = ({
  defaultImage,
  onUpdate,
  classNameImageContainer,
  helperText,
  hideDesc,
  commentType,
  error,
  title = 'Photo',
  backgroundColor,
}) => {
  const [previewImg, setPreviewImg] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleImageClick = () => {
    fileRef.current?.click();
  };

  const handleFileUpload = async (uploadedFile: File) => {
    if (uploadedFile.size <= 4194304) {
      setIsUploading(true);
      try {
        const response = await usersApi.uploadFile({
          contentType: uploadedFile.type,
          name: 'avatarurl',
          type: commentType ? commentType : 'image-avatar',
        });
        const { uploadURL, file_id } = response;

        const uploadResponse = await fetch(uploadURL, {
          method: 'PUT',
          headers: {
            'Content-Type': uploadedFile.type,
          },
          body: uploadedFile,
        });

        if (!uploadResponse.ok) {
          throw new Error(
            `Upload failed with status: ${uploadResponse.status}`
          );
        }
        const previewUrl = URL.createObjectURL(uploadedFile);
        setPreviewImg(previewUrl);
        onUpdate(file_id);
      } catch (error) {
        console.error('Error uploading file:', error);
        alert('Error uploading file. Check the console for more details.');
      } finally {
        setIsUploading(false);
      }
    } else {
      alert('File size should not exceed 4MB');
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];
    if (uploadedFile) {
      await handleFileUpload(uploadedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      await handleFileUpload(file);
    }
  };

  return (
    <div
      className={styles.container}
      style={{
        borderBottom: hideDesc ? 'none' : '1x solid #d5d5dc',
        paddingBottom: hideDesc ? '0px' : '20px',
      }}
    >
      <div className={styles.text}>
        {title && <div className={styles.title}>{title}</div>}
        {/* {!hideDesc && (
          <div>
            <a
              href="/how-it-works#info-photo"
              target="_blank"
              rel="noopener noreferrer"
            >
              <InfoOutlinedIcon style={{ color: 'red', cursor: 'pointer' }} />
            </a>
          </div>
        )} */}
      </div>
      {!hideDesc && (
        <div className={styles.description}>
          Dunbar148 is all about authenticity, so most Members use a real photo
          of themselves.
        </div>
      )}
      <div
        className={classNames(styles.photoContainer, classNameImageContainer, {
          [styles.dragging]: isDragging,
        })}
        style={{
          backgroundImage: previewImg
            ? `url(${previewImg})`
            : `url(${defaultImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          ...(backgroundColor && { backgroundColor }),
        }}
        onClick={handleImageClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {!previewImg && !isUploading && !defaultImage && (
          <div className={styles.photoWrapper}>
            <PhotoCameraOutlinedIcon
              style={{ color: '#888', width: '30px', height: '30px' }}
            />
            <div className={styles.uploadText}>Select photo</div>
            <div className={styles.dragText}>Or drag and drop</div>
          </div>
        )}
        {isUploading && (
          <div className={styles.photoWrapper}>
            <MaterialCircularProgress
              size={30}
              height="inherit"
              message="Uploading..."
            />
          </div>
        )}
      </div>
      {!previewImg && !hideDesc && (
        <div className="upload-image__description">{helperText}</div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept=".jpg, .jpeg, .png"
        className={styles.hiddenInput}
        onChange={handleInputChange}
      />

      <small className={styles.formHint}>
        {error && <span className={styles.invalidFeedback}>{error}</span>}
      </small>
    </div>
  );
};
